import * as XLSX from "xlsx";
import { saveAs } from "file-saver-es";

/** Background and text color of one cell, as "AARRGGBB" */
export interface CellStyle {
  background: string;
  fontColor: string;
}

const xlsxMimeType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** Write the book as a file and download it, keeping the colors that were set on the
 * cells. xlsx reads cell styles but does not write them, so they are added to the file
 * after xlsx has written it. */
export function writeStyledFile(book: XLSX.WorkBook, fileName: string) {
  const content = new Uint8Array(
    XLSX.write(book, { type: "array", bookType: "xlsx" })
  );

  saveAs(
    new Blob([addCellStyles(book, content)], { type: xlsxMimeType }),
    fileName
  );
}

/** Add the styles of the cells to the file written by xlsx */
function addCellStyles(book: XLSX.WorkBook, content: Uint8Array) {
  const { styles, refsBySheet } = collectCellStyles(book);

  if (styles.length === 0) {
    return content;
  }

  const container = XLSX.CFB.read(content, { type: "array" });
  const firstStyleIndex = addStylesToStyleSheet(container, styles);

  refsBySheet.forEach((refs, sheetIndex) => {
    if (refs.size > 0) {
      setStylesOnCells(container, sheetIndex, refs, firstStyleIndex);
    }
  });

  return XLSX.CFB.write(container, { fileType: "zip", type: "array" });
}

/** Collect the styles of all cells of the book. The same color is used by many cells,
 * so every color is kept only once and the cells refer to it by its index. */
function collectCellStyles(book: XLSX.WorkBook) {
  const styles: CellStyle[] = [];
  const indexByColor = new Map<string, number>();
  const refsBySheet: Map<string, number>[] = [];

  book.SheetNames.forEach((name) => {
    const sheet = book.Sheets[name];
    const refs = new Map<string, number>();

    Object.keys(sheet).forEach((ref) => {
      // keys like !ref or !cols are properties of the sheet and not cells
      if (ref.startsWith("!")) {
        return;
      }

      const style = sheet[ref].s as CellStyle | undefined;

      if (style == null) {
        return;
      }

      const color = style.background + style.fontColor;
      let index = indexByColor.get(color);

      if (index == null) {
        index = styles.length;
        styles.push(style);
        indexByColor.set(color, index);
      }

      refs.set(ref, index);
    });

    refsBySheet.push(refs);
  });

  return { styles, refsBySheet };
}

/** Append a fill, a font and a cell format for every style to styles.xml. The entries
 * xlsx already wrote, like the date formats, keep their index and stay valid.
 * Returns the index of the first appended cell format. */
function addStylesToStyleSheet(container: any, styles: CellStyle[]) {
  const path = "/xl/styles.xml";
  let xml = readPart(container, path);

  const firstFill = getCount(xml, "fills");
  const firstFont = getCount(xml, "fonts");
  const firstStyle = getCount(xml, "cellXfs");

  const fills = styles
    .map(
      (style) =>
        `<fill><patternFill patternType="solid"><fgColor rgb="${style.background}"/>` +
        `<bgColor indexed="64"/></patternFill></fill>`
    )
    .join("");

  const fonts = styles
    .map(
      (style) =>
        `<font><sz val="11"/><name val="Calibri"/><color rgb="${style.fontColor}"/></font>`
    )
    .join("");

  const cellFormats = styles
    .map(
      (style, index) =>
        `<xf numFmtId="0" fontId="${firstFont + index}" fillId="${
          firstFill + index
        }" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1">` +
        `<alignment horizontal="center" vertical="center"/></xf>`
    )
    .join("");

  xml = setCount(xml, "fills", firstFill + styles.length).replace(
    "</fills>",
    fills + "</fills>"
  );
  xml = setCount(xml, "fonts", firstFont + styles.length).replace(
    "</fonts>",
    fonts + "</fonts>"
  );
  xml = setCount(xml, "cellXfs", firstStyle + styles.length).replace(
    "</cellXfs>",
    cellFormats + "</cellXfs>"
  );

  writePart(container, path, xml);

  return firstStyle;
}

/** Let the cells of one sheet refer to the formats added to styles.xml */
function setStylesOnCells(
  container: any,
  sheetIndex: number,
  refs: Map<string, number>,
  firstStyleIndex: number
) {
  const path = `/xl/worksheets/sheet${sheetIndex + 1}.xml`;
  const xml = readPart(container, path);

  if (xml === "") {
    return;
  }

  // all cells are handled in one pass, a replace per cell would be too slow on the
  // large exports
  const withStyles = xml.replace(
    /<c r="([A-Z]+\d+)"([^>]*)>/g,
    (cell: string, ref: string, attributes: string) => {
      const style = refs.get(ref);

      if (style == null) {
        return cell;
      }

      const index = firstStyleIndex + style;
      // a cell without a value is written as <c r="A1"/>
      const isSelfClosing = attributes.endsWith("/");
      const rest = isSelfClosing ? attributes.slice(0, -1) : attributes;
      const withStyle = / s="\d+"/.test(rest)
        ? rest.replace(/ s="\d+"/, ` s="${index}"`)
        : `${rest} s="${index}"`;

      return `<c r="${ref}"${withStyle}${isSelfClosing ? "/" : ""}>`;
    }
  );

  writePart(container, path, withStyles);
}

function readPart(container: any, path: string) {
  const part = XLSX.CFB.find(container, path);

  if (part == null || part.content == null) {
    return "";
  }

  return new TextDecoder().decode(new Uint8Array(part.content));
}

function writePart(container: any, path: string, xml: string) {
  XLSX.CFB.utils.cfb_add(container, path, new TextEncoder().encode(xml));
}

function getCount(xml: string, tag: string) {
  const match = xml.match(new RegExp(`<${tag} count="(\\d+)"`));
  return match == null ? 0 : Number(match[1]);
}

function setCount(xml: string, tag: string, count: number) {
  return xml.replace(
    new RegExp(`<${tag} count="\\d+"`),
    `<${tag} count="${count}"`
  );
}
